import { useCallback, useEffect, useRef, useState } from 'react';
import { authDelete, authGet, authPost } from '../../../services/apiService';

const BASE = '/api/release-cadence';

const unwrap = (response, fallback) => {
  if (!response || response.success === false) throw new Error((response && response.message) || fallback);
  return response;
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.readAsDataURL(file);
  });

/**
 * Loads one endpoint and exposes reload. Data is tied to the request (url + params) that produced it, so changing
 * params never shows the previous params' data, and a slow older response cannot overwrite a newer one.
 * A failed reload keeps the data already shown and reports refreshError; error is only set when there is no data.
 */
const useEndpoint = (url, params, enabled = true) => {
  const [state, setState] = useState({ key: null, data: null, error: '', refreshError: '', loading: enabled });
  const latest = useRef(0);
  const key = `${url}|${JSON.stringify(params || {})}`;

  const load = useCallback(async () => {
    if (!enabled) return;
    latest.current += 1;
    const id = latest.current;
    setState((s) => ({ ...s, loading: true }));
    try {
      const data = unwrap(await authGet(url, params, 'json', { useCache: false }), 'Failed to load');
      if (id !== latest.current) return;
      setState({ key, data, error: '', refreshError: '', loading: false });
    } catch (err) {
      if (id !== latest.current) return;
      const message = err.message || 'Failed to load';
      setState((s) => (s.key === key && s.data ? { ...s, refreshError: message, loading: false } : { key, data: null, error: message, refreshError: '', loading: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(
    () => () => {
      latest.current += 1;
    },
    []
  );

  const current = state.key === key ? state : null;
  return {
    data: current ? current.data : null,
    loading: enabled && (state.loading || !current),
    error: current ? current.error : '',
    refreshError: current ? current.refreshError : '',
    reload: load
  };
};

export const useOverview = () => useEndpoint(`${BASE}/overview`);
export const useCommitment = () => useEndpoint(`${BASE}/commitment`);
export const useVersionCatalog = () => useEndpoint(`${BASE}/versions`);
export const useWaves = (version) => useEndpoint(`${BASE}/waves`, { version }, !!version);
export const useReadiness = (version) => useEndpoint(`${BASE}/readiness`, { version }, !!version);
export const useReadinessTrend = () => useEndpoint(`${BASE}/readiness-trend`);
export const usePlanComparison = (planId) => useEndpoint(`${BASE}/plans/${planId}/comparison`, null, !!planId);

export const useConfidence = (audience) => useEndpoint(`${BASE}/confidence`, { audience });

export const startTestEvidenceSync = async (force = false) =>
  unwrap(await authPost(`${BASE}/test-evidence/sync`, force ? { force: true } : {}), 'Could not start the test sync');

export const useHealth = (version) => useEndpoint(`${BASE}/health`, { version }, !!version);
export const useMicroservices = () => useEndpoint(`${BASE}/health/microservices`);

export const startHealthSync = async () => unwrap(await authPost(`${BASE}/health/sync`, {}), 'Could not start the health sync');

export const syncFromDatadog = async (lookbackDays) =>
  unwrap(await authPost(`${BASE}/sync`, lookbackDays ? { lookbackDays } : {}), 'Sync failed').sync;

export const uploadPlan = async (file) =>
  unwrap(await authPost(`${BASE}/plans`, { fileName: file.name, contentBase64: await fileToBase64(file) }), 'Upload failed');

export const uploadSummary = async (file) =>
  unwrap(await authPost(`${BASE}/summaries`, { fileName: file.name, contentBase64: await fileToBase64(file) }), `Could not read ${file.name}`);

export const deletePlan = async (id) => unwrap(await authDelete(`${BASE}/plans/${id}`), 'Delete failed');
